import React, { useEffect, useState, useRef } from 'react';
import './Loader.css';

const COUNT_MS = 1600; // 0→360 count-up, then holds while the ring keeps spinning
const TOTAL_DOTS = 13;

/**
 * Brand loader: a comet-trail ring of dots (pure CSS rotation — GPU-cheap,
 * no per-frame React state) around a 0→360° count-up. The count runs once
 * and stops; the ring spins for as long as loading takes.
 */
const Loader = ({ fullscreen = true, onComplete }) => {
  const [count, setCount] = useState(0);
  const doneRef = useRef(false);

  useEffect(() => {
    let rafId;
    let start = null;
    const tick = (now) => {
      if (start === null) start = now;
      const progress = Math.min((now - start) / COUNT_MS, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(eased * 360);
      // Only touch state when the visible integer changes
      setCount((prev) => (prev === value ? prev : value));
      if (progress < 1) {
        rafId = requestAnimationFrame(tick);
      } else if (!doneRef.current) {
        doneRef.current = true;
        onComplete?.();
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [onComplete]);

  return (
    <div className={`loader-overlay ${fullscreen ? 'fullscreen' : ''}`} role="status" aria-label="Duke u ngarkuar">
      <div className="spinner-container">
        <div className="dot-ring">
          {Array.from({ length: TOTAL_DOTS }).map((_, idx) => (
            <div
              key={idx}
              className="dot"
              style={{
                '--angle': `${(360 / TOTAL_DOTS) * idx}deg`,
                // Comet trail: head is brightest, fading around the ring
                opacity: 0.18 + 0.82 * (idx / (TOTAL_DOTS - 1))
              }}
            />
          ))}
        </div>
        <div className={`center-number ${count === 360 ? 'settled' : ''}`}>{count}°</div>
      </div>
    </div>
  );
};

export default Loader;
